'use client';

import Link from '~/(tenant)/_components/tenant-link';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { EmptyState, SectionHeader } from '~/(tenant)/_components/native/_primitives';
import { getFamilyBookingsAction } from '~/(tenant)/bookings/_lib/server/family-bookings-action';

import pathsConfig from '~/config/paths.config';

interface BookingRecord {
  id: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  booking_status: string;
  participant_status?: string;
  court_name?: string;
  class_name?: string;
  is_class_enrollment?: boolean;
}

export function MyBookingsSection({ accountId }: { accountId: string }) {
  const t = useNativeTokens();
  const client = useSupabase();
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
  });

  const isAuthenticated = !!session;
  const userId = session?.user?.id;

  const { data: bookings, isLoading } = useQuery({
    queryKey: ['native-home-bookings', userId, accountId],
    queryFn: async () => {
      const result = await getFamilyBookingsAction({ includePast: false });

      if (!result.success || !result.data) return [] as BookingRecord[];

      const cutoff = new Date();
      if (cutoff.getHours() < 6) {
        cutoff.setDate(cutoff.getDate() - 1);
      }
      cutoff.setHours(6, 0, 0, 0);

      return ((result.data as any[])
        .flatMap((account: any) => account.bookings || [])
        .filter((b: any) => {
          const status = b.is_class_enrollment ? b.participant_status : b.booking_status;
          if (!['confirmed', 'pending'].includes(status)) return false;
          const dt = new Date(`${b.booking_date}T${b.start_time}`);
          const [hour] = (b.start_time || '').split(':').map(Number);
          if ((hour ?? 0) < 6) dt.setDate(dt.getDate() + 1);
          return dt > cutoff;
        })
        .sort((a: any, b: any) => {
          const dtA = new Date(`${a.booking_date}T${a.start_time}`).getTime();
          const dtB = new Date(`${b.booking_date}T${b.start_time}`).getTime();
          return dtA - dtB;
        })
        .slice(0, 3) as BookingRecord[]);
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 60_000,
  });

  return (
    <section style={{ paddingTop: 32 }}>
      <SectionHeader
        eyebrow={<Trans i18nKey="common:nativeHome.yourSchedule" defaults="Your schedule" />}
        title={<Trans i18nKey="common:bookings" defaults="My bookings" />}
        action={
          isAuthenticated
            ? {
                label: <Trans i18nKey="common:seeAll" defaults="View" />,
                href: '/bookings',
              }
            : undefined
        }
      />

      <div style={{ marginTop: 18, padding: '0 22px' }}>
        {!isAuthenticated ? (
          <Link
            href={pathsConfig.auth.signIn}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '32px 24px',
              border: `1px solid ${t.line}`,
              borderRadius: t.radius,
              color: t.muted,
              fontFamily: t.fontSans,
              fontSize: 13,
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            <Trans
              i18nKey="common:nativeHome.signInToSeeBookings"
              defaults="Sign in to see your bookings"
            />
          </Link>
        ) : isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1].map((i) => (
              <div
                key={i}
                style={{
                  height: 88,
                  borderRadius: t.radius,
                  backgroundColor: t.line,
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        ) : !bookings || bookings.length === 0 ? (
          <EmptyState
            mark="quiet"
            eyebrow={<Trans i18nKey="common:nativeHome.aBlankCalendar" defaults="A blank calendar" />}
            title={<Trans i18nKey="common:nativeHome.scheduleQuietPart1" defaults="Your schedule is" />}
            emphasis={<Trans i18nKey="common:nativeHome.scheduleQuietPart2" defaults="quiet." />}
            body={
              <Trans
                i18nKey="common:nativeHome.scheduleQuietBody"
                defaults="When you reserve a class, it will appear here."
              />
            }
            cta={{
              label: <Trans i18nKey="common:bookNow" defaults="Reserve a class" />,
              href: '/book',
            }}
          />
        ) : (
          <>
            {bookings.map((booking, i) => (
              <BookingRow key={booking.id} booking={booking} isFirst={i === 0} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function BookingRow({
  booking,
  isFirst,
}: {
  booking: BookingRecord;
  isFirst: boolean;
}) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation();
  const bookingDate = new Date(booking.booking_date + 'T00:00:00');
  const dayLabel = formatLocaleDate(bookingDate, i18n.language, { weekday: 'short', day: undefined, month: undefined, year: undefined });
  const dayNum = bookingDate.getDate();
  const monthLabel = formatLocaleDate(bookingDate, i18n.language, { month: 'short', day: undefined, year: undefined });

  const title = booking.is_class_enrollment
    ? booking.class_name
    : (booking.court_name ?? tr('bookings:courtLabel'));

  return (
    <Link
      href="/bookings"
      style={{
        display: 'flex',
        gap: 16,
        padding: '20px 0',
        borderTop: isFirst ? `1px solid ${t.line}` : 'none',
        borderBottom: `1px solid ${t.line}`,
        textDecoration: 'none',
        color: t.foreground,
      }}
    >
      <div
        style={{
          width: 56,
          textAlign: 'center',
          paddingTop: 2,
          paddingInlineEnd: 12,
          borderInlineEnd: `1px solid ${t.line}`,
        }}
      >
        <div style={{ fontFamily: t.fontMono, fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: t.muted }}>
          {dayLabel}
        </div>
        <div style={{ fontFamily: t.fontDisplay, fontSize: 30, fontWeight: 300, lineHeight: 1, marginTop: 2 }}>
          {dayNum}
        </div>
        <div style={{ fontFamily: t.fontMono, fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: t.muted, marginTop: 4 }}>
          {monthLabel}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: t.fontDisplay, fontSize: 19, fontWeight: 400, lineHeight: 1.2 }}>
          {title}
        </div>
        <div style={{ fontFamily: t.fontSans, fontSize: 12, color: t.muted, marginTop: 4 }}>
          {formatTimeToAmPm(booking.start_time, i18n.language)} — {formatTimeToAmPm(booking.end_time, i18n.language)}
        </div>
      </div>
    </Link>
  );
}
